import * as React from 'react';
import { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { ActionButtonVariants } from 'components/action-button';
import { GeolocationDeniedModal } from 'components/geolocation-permission-modal/geolocation-denied-modal';
import {
  InitialAnimation,
  LoadingAnimation,
  TransitionAnimation,
} from 'components/lottie-animations/reset-location';
import { useGeolocation } from 'state/geolocation';

import { Button } from './reset-location-button.styled';
import { IResetLocationButtonProps } from './types';

export const ResetLocationButton: React.FC<IResetLocationButtonProps> = ({ onClick, loading }) => {
  const [status, setStatus] = useState<'idle' | 'transitioning'>('idle');
  const { formatMessage } = useIntl();

  const {
    isPermissionGranted,
    isPermissionPrompt,
    isPermissionDenied,
    setPermissionChecking,
    loadCurrentPosition,
  } = useGeolocation();

  const [geolocationDeniedModalOpen, setGeolocationDeniedModalOpen] = useState(false);

  const dismissModal = () => setGeolocationDeniedModalOpen(false);

  const handleClick = () => {
    setStatus('transitioning');
    if (isPermissionDenied) {
      setGeolocationDeniedModalOpen(true);
    } else if (isPermissionPrompt) {
      loadCurrentPosition();
      setPermissionChecking();
    }
  };

  const getStartIcon = () => {
    if (loading) {
      return <LoadingAnimation />;
    }

    if (status === 'transitioning') {
      return (
        <TransitionAnimation
          onComplete={() => {
            onClick();
            setStatus('idle');
          }}
        />
      );
    }

    return <InitialAnimation />;
  };

  useEffect(() => {
    if (isPermissionGranted) {
      setGeolocationDeniedModalOpen(false);
    }
  }, [isPermissionGranted]);

  useEffect(() => {
    if (isPermissionDenied) {
      setGeolocationDeniedModalOpen(true);
    }
  }, [isPermissionDenied]);

  return (
    <>
      <GeolocationDeniedModal isOpen={geolocationDeniedModalOpen} dismissModal={dismissModal} />
      <Button
        aria-label={formatMessage({ id: 'resetLocationButtonAria' })}
        data-testid="reset-location-button"
        onClick={handleClick}
        variant={ActionButtonVariants.BOX_SHADOW_INVERSE}
        startIcon={getStartIcon()}
        disabled={loading}
        className={`${loading ? 'loading' : ''}`}
      />
    </>
  );
};
